'use client';

import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import FullScreenGalleryViewer from './full_screen_gallery_viewer';
import { normalizeImageUrl } from '../lib/util';
import OptimizedImage from '../components/OptimizedImage';

interface ImageGalleryWithModalProps {
  title: string;
  images: string[];
  altTexts?: Record<string, string>;
}

const ImageGalleryWithModal: React.FC<ImageGalleryWithModalProps> = ({
  images,
  title = '',
  altTexts = {},
}) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);

  // Get all variants for a normalized URL
  const getAllVariants = (normalizedUrl: string, urls: string[]): string[] => {
    return urls.filter(url => normalizeImageUrl(url) === normalizedUrl);
  };

  // Get unique normalized URLs in current order
  const getUniqueNormalizedUrls = (urls: string[]): string[] => {
    const seen = new Set<string>();
    return urls.map(url => normalizeImageUrl(url))
      .filter(normalizedUrl => {
        if (!normalizedUrl || seen.has(normalizedUrl)) return false;
        seen.add(normalizedUrl);
        return true;
      });
  };

  // Process images to find size variants
  const processImages = (imageUrls: string[]) => {
    const normalizedUrls = getUniqueNormalizedUrls(imageUrls);

    return normalizedUrls.map(normalizedUrl => {
      const variants = getAllVariants(normalizedUrl, imageUrls);

      // For new URL pattern (venues/venue_id/section_id/image.ext)
      if (normalizedUrl.startsWith('venues/')) {
        const basePath = normalizedUrl.replace(/\.[^.]+$/, ''); // Remove extension
        const extension = normalizedUrl.match(/\.([^.]+)$/)?.[0] || '';

        return {
          thumbnail: variants.find(url => url.includes('_800x800')) ||
            `${basePath}_800x800${extension}` ||
            variants[0],
          fullsize: variants.find(url => url.includes('_1600x1600')) ||
            `${basePath}_1600x1600${extension}` ||
            variants[0]
        };
      }

      // For traditional URLs
      return {
        thumbnail: variants.find(url => url.includes('_800x800')) || variants[0],
        fullsize: variants.find(url => url.includes('_1600x1600')) || variants[0]
      };
    });
  };

  const uniqueImages = processImages(images);

  const handleOpenGallery = (index: number) => {
    setInitialIndex(index);
    setIsGalleryOpen(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        {uniqueImages.length > 0
          ? uniqueImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <div className="relative w-full h-32" onClick={() => handleOpenGallery(index)}>
                <OptimizedImage
                  src={image.thumbnail}
                  alt={altTexts[image.thumbnail] || `${title} image ${index + 1}`}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className='object-cover rounded-lg cursor-pointer shadow-md transition-shadow duration-300 hover:shadow-lg'
                  priority={index < 4} // Load first 4 images with priority
                />
              </div>
            </Grid>
          ))
          : [...Array(5)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Box className='w-full h-32 bg-gray-300 rounded-lg animate-pulse' />
            </Grid>
          ))}
      </Grid>
      {isGalleryOpen && (
        <FullScreenGalleryViewer
          images={uniqueImages.map(img => img.fullsize)}
          initialIndex={initialIndex}
          onClose={handleCloseGallery}
          title={title}
          altTexts={altTexts}
        />
      )}
    </Box>
  );
};

export default ImageGalleryWithModal;
